import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';

// TODO: Viresh: - use Datepicker atom here https://bitbucket.org/evinops-hafla/hafla/pull-requests/4166#comment-400319651

import {
  CheckoutActionButton,
  CheckoutDisclaimerModal,
  EventDateTime,
  RequestCallModal
} from '@/components/atomic/atoms';
import {
  CheckoutEventDetail,
  CheckoutUserInfo,
  ToastContainer
} from '@/components/atomic/molecules';
import { useUIContext } from '@/components/context/uiContext/ManagedUiContext';
import config from '@/config';
import { useIsMobile } from '@/helpers/screenResolutionHelper';
import { captureGTMEventAddShippingInfo } from '@/lib/gtm';
import {
  createDateWithTime,
  currentDate,
  formatTime,
  getDurationBetweenDates,
  parseDateWithMoment
} from '@/lib/time';
import {
  defaultCartCheckout,
  getCheckoutEventsAPI
} from '@/services/hostCart.service';
import { getPageURL, ROUTE_CONFIG } from '@/services/hostPortal.service';
import { getAuthIdentity } from '@/services/identity.service';
import { sendPriorityEventInquiryDetails } from '@/services/inquiry.service';

const userAddressObject = {
  buildingName: '',
  cityId: '',
  formattedAddress: '',
  houseNumber: '',
  landmark: '',
  latitude: 0,
  locationSelected: false,
  longitude: 0
};

const CheckoutEventContactInfoOrganism = ({
  currentStep,
  setCurrentStep,
  setLoading,
  setStepReached,
  stepReached
}) => {
  const { id: cartId, cartItems, defaultHostCartDetail } = useUIContext();

  const [isMobile] = useIsMobile();
  const Router = useRouter();

  const [showToast, setShowToast] = useState({
    message: '',
    show: false,
    status: false
  });
  const [userAddress, setUserAddress] = useState(userAddressObject);
  const [eventDetails, setEventDetails] = useState();
  const [isRequestCallBackApplicable, setIsRequestCallBackApplicable] =
    useState(false);
  const [isRequestCallBackModalVisible, setIsRequestCallBackModalVisible] =
    useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [tapSanitizedToken, setTapSanitizedToken] = useState(null);
  const [renderTapPaymentWidget, setRenderTapPaymentWidget] = useState(false);

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    trigger,
    watch
  } = useForm({
    defaultValues: {
      checkoutEventId: '',
      contactEmail: '',
      contactName: '',
      eventAddress: {
        buildingName: '',
        cityId: '',
        formattedAddress: '',
        houseNumber: '',
        landmark: '',
        latitude: 0,
        longitude: 0
      },
      eventDate: '',
      eventTime: ''
    }
  });

  const { isValid } = useFormState({
    control
  });

  const watchedEventDate = watch('eventDate');
  const watchedEventTime = watch('eventTime');

  useEffect(() => {
    if (watchedEventDate && watchedEventTime) {
      const formattedEventDateTime = parseDateWithMoment(
        createDateWithTime(watchedEventDate, watchedEventTime)
      );
      const duration = getDurationBetweenDates({
        date: formattedEventDateTime,
        referenceDate: currentDate
      });
      const shouldShowRequestCallbackModal = duration.asMinutes() <= 24 * 60;
      setIsRequestCallBackApplicable(shouldShowRequestCallbackModal);
      if (shouldShowRequestCallbackModal) {
        setIsRequestCallBackModalVisible(true);
      }
    }
  }, [watchedEventDate, watchedEventTime]);

  const createPriorityLeadData = async (callback) => {
    const cartItemList =
      cartItems?.length > 0
        ? cartItems.map(({ product, quantity }) => ({ product, quantity }))
        : [];

    const formattedEventTime = formatTime(getValues('eventTime'));

    const auth = getAuthIdentity();
    return callback({
      cartItems: cartItemList,
      eventDetails: {
        eventDate: getValues('eventDate'),
        eventTime: formattedEventTime
      },
      userId: auth.id
    });
  };

  const handleReqCallBackClick = async () => {
    const { message, status } = await createPriorityLeadData(
      sendPriorityEventInquiryDetails
    );
    setIsRequestCallBackModalVisible(false);
    if (status) {
      Router.push(
        getPageURL({
          pageName: ROUTE_CONFIG.CHECKOUT_CALLBACK_SUCCESS.label
        })
      );
    } else {
      setShowToast({
        message:
          message || 'Failed to create priority lead. Please try again later.',
        show: true,
        status: false
      });
    }
  };

  const handleEventInfoSubmit = async (data) => {
    if (!config.isRenderTapPaymentWidget || Boolean(tapSanitizedToken?.id)) {
      setLoading(true);
      const auth = getAuthIdentity();
      const { entity, message, status } = await defaultCartCheckout({
        params: {
          cartId,
          userCartId: auth.id
        },
        body: {
          tapDetails: { sanitizedToken: tapSanitizedToken },
          userEventDetails: {
            checkoutEventId: data.checkoutEventId,
            eventAddress: userAddress,
            eventContactEmail: data.contactEmail,
            eventContactName: data.contactName,
            eventDate: data.eventDate,
            eventStartTime: data.eventTime
          }
        }
      });
      const {
        cartItems: itemList,
        derivedValues: { orderTotal },
        promoCode: { code } = {}
      } = defaultHostCartDetail;

      if (status) {
        userAddress &&
          captureGTMEventAddShippingInfo({
            code,
            itemList,
            orderTotal
          });

        if (isMobile) {
          setCurrentStep(currentStep + 1);
          setStepReached(stepReached + 1);
        }
        Router.replace(entity.checkoutUrl);
      } else {
        setLoading(false);
        setShowToast({
          message,
          show: true,
          status
        });
      }
    }
  };

  useEffect(() => {
    if (tapSanitizedToken?.id) {
      const formValues = getValues();
      handleEventInfoSubmit(formValues);
    }
  }, [tapSanitizedToken?.id]);

  const moveSelectedEventToTop = ({ checkoutEvents, selectedEventId }) => {
    const selectedEvent = checkoutEvents.find(
      (event) => event.id === selectedEventId
    );

    if (!selectedEvent) {
      return checkoutEvents;
    }

    const otherEvents = checkoutEvents.filter(
      (event) => event.id !== selectedEventId
    );

    return [selectedEvent, ...otherEvents];
  };

  const showCheckoutForm = async () => {
    const events = await getCheckoutEventsAPI();
    const rearrangedEvents = moveSelectedEventToTop({
      checkoutEvents: events?.entity ?? [],
      selectedEventId: getValues('checkoutEventId')
    });
    setEventDetails(rearrangedEvents);
    events.entity = rearrangedEvents;
    return events;
  };

  useEffect(() => {
    showCheckoutForm();
  }, [currentStep]);

  return (
    <div className='sm:p-8'>
      <ToastContainer {...{ showToast, setShowToast }} />
      <CheckoutEventDetail
        {...{
          clearErrors,
          control,
          errors,
          eventDetails,
          getValues,
          register,
          setValue
        }}
      />
      <EventDateTime
        {...{
          control,
          errors,
          isMobile,
          setValue,
          watch
        }}
      />
      <CheckoutUserInfo
        {...{
          currentStep,
          errors,
          register,
          setUserAddress,
          setValue,
          trigger,
          userAddress
        }}
      />
      {showDisclaimerModal && (
        <CheckoutDisclaimerModal {...{ setShowDisclaimerModal }} />
      )}
      {isRequestCallBackModalVisible && (
        <RequestCallModal
          {...{
            handleReqCallBackClick,
            isRequestCallBackModalVisible,
            setIsRequestCallBackModalVisible
          }}
        />
      )}
      <CheckoutActionButton
        {...{
          handleEventInfoSubmit: handleSubmit(handleEventInfoSubmit),
          handleReqCallBackClick,
          hostEmail: watch('contactEmail'),
          hostMobile: getAuthIdentity()?.mobile,
          hostName: watch('contactName'),
          isRequestCallBackApplicable,
          isValid,
          renderTapPaymentWidget,
          setLoading,
          setRenderTapPaymentWidget,
          setShowToast,
          setTapSanitizedToken
        }}
      />
    </div>
  );
};

export default CheckoutEventContactInfoOrganism;
